import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Search, RotateCcw, Download, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { downloadProjectExport } from "@/lib/project-import-export";
import { ImportProjectButton } from "@/components/project/import-project-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

import type { Project } from "@shared/schema";
import { formatActivityLabel } from "@/lib/formatters";

const projectTypes = [
  { value: "all", label: "Alle Tätigkeiten" },
  { value: "betoninstandsetzung", label: "Betoninstandsetzung" },
  { value: "oberflachenbehandlung", label: "Oberflächenbehandlung" },
  { value: "rissabdichtung", label: "Rissabdichtung" },
  { value: "korrosionsschutz", label: "Korrosionsschutz" },
  { value: "strukturverstaerkung", label: "Strukturverstärkung" },
  { value: "schutzschichten", label: "Schutzschichten auftragen" },
  { value: "wasserschutz", label: "Wasserschutz" },
  { value: "brandschutz", label: "Brandschutz" },
];

const statusOptions = [
  { value: "all", label: "Alle Status" },
  { value: "draft", label: "Entwurf" },
  { value: "active", label: "In Bearbeitung" },
  { value: "completed", label: "Abgeschlossen" },
  { value: "archived", label: "Archiviert" },
];

const VALID_STATUS_VALUES = new Set(["all", "draft", "active", "completed", "archived"]);

export default function ProjectSearchPage() {
  const [, navigate] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [hasSearched, setHasSearched] = useState(false);
  const { toast } = useToast();
  const [exportingId, setExportingId] = useState<number | null>(null);

  const handleExport = async (project: Project) => {
    setExportingId(project.id);
    try {
      await downloadProjectExport(project.id, project.name);
      toast({ title: "Export erstellt", description: `„${project.name}" wurde heruntergeladen.` });
    } catch (err) {
      toast({
        title: "Export fehlgeschlagen",
        description: (err as Error).message || "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setExportingId(null);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const statusParam = params.get("status");
    if (statusParam && VALID_STATUS_VALUES.has(statusParam)) {
      setSelectedStatus(statusParam);
      setHasSearched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: searchResults, isLoading } = useQuery<Project[]>({
    queryKey: ["/api/projects/search", { 
      query: searchQuery, 
      type: selectedType === "all" ? "" : selectedType, 
      status: selectedStatus === "all" ? "" : selectedStatus 
    }],
    enabled: hasSearched,
  });

  const handleSearch = () => {
    setHasSearched(true);
  };

  const handleReset = () => {
    setSearchQuery("");
    setSelectedType("all");
    setSelectedStatus("all");
    setHasSearched(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-bauwerk-mint text-bauwerk-teal";
      case "completed":
        return "bg-green-100 text-green-800";
      case "draft":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
        return "In Bearbeitung";
      case "completed":
        return "Abgeschlossen";
      case "draft":
        return "Entwurf";
      case "archived":
        return "Archiviert";
      default:
        return status;
    }
  };

  const getTypeText = (type: string) => formatActivityLabel(type);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-bauwerk-teal mb-2">
              Projekte suchen
            </h1>
            <p className="text-gray-600">
              Finden Sie bestehende Projekte und Erfassungen
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <ImportProjectButton />
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zum Dashboard
            </Button>
          </div>
        </div>

        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="search">Suchbegriff</Label>
                <div className="relative">
                  <Input
                    id="search"
                    placeholder="Projektname, Ort, Auftraggeber..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Tätigkeit</Label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="focus:ring-bauwerk-teal focus:border-bauwerk-teal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {projectTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="focus:ring-bauwerk-teal focus:border-bauwerk-teal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-between items-center mt-6">
              <div className="flex space-x-4">
                <Button
                  onClick={handleSearch}
                  disabled={isLoading}
                  className="bg-bauwerk-orange hover:bg-bauwerk-light-orange"
                >
                  <Search className="w-4 h-4 mr-2" />
                  {isLoading ? "Suchen..." : "Suchen"}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Filter zurücksetzen
                </Button>
              </div>
              <span className="text-sm text-gray-600">
                {hasSearched ? `${searchResults?.length || 0} Projekte gefunden` : ""}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-bauwerk-teal">Suchergebnisse</CardTitle>
          </CardHeader>
          <CardContent>
            {!hasSearched ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-white" />
                </div>
                <p className="text-gray-600 mb-4">Starten Sie eine Suche</p>
                <p className="text-sm text-gray-500">
                  Verwenden Sie die Filter oben, um Projekte zu finden
                </p>
              </div>
            ) : isLoading ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-white animate-spin" />
                </div>
                <p className="text-gray-600">Suche läuft...</p>
              </div>
            ) : !searchResults || searchResults.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-white" />
                </div>
                <p className="text-gray-600 mb-4">Keine Projekte gefunden</p>
                <p className="text-sm text-gray-500">
                  Versuchen Sie andere Suchbegriffe oder erstellen Sie ein neues Projekt
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {searchResults.map((project) => (
                  <div
                    key={project.id}
                    className="flex justify-between items-center p-4 border rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div>
                      <h4 className="font-semibold text-bauwerk-teal mb-1">
                        {project.name}
                      </h4>
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">Tätigkeit:</span> {getTypeText(project.type)}
                      </p>
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">Adresse:</span> {project.address}
                      </p>
                      {project.customer && (
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Auftraggeber:</span> {project.customer}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        {project.createdAt ? new Date(project.createdAt).toLocaleDateString('de-DE') : ''}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className={getStatusColor(project.status)}>
                        {getStatusText(project.status)}
                      </Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-gray-500 hover:text-bauwerk-teal hover:bg-bauwerk-mint/40"
                        aria-label={`Projekt ${project.name} exportieren`}
                        disabled={exportingId === project.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExport(project);
                        }}
                        data-testid={`button-export-project-${project.id}`}
                      >
                        {exportingId === project.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
    </div>
  );
}
